import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';

const HEART_HOTSPOTS = [
  {
    id: 'hotspot-svc',
    label: 'Superior Vena Cava',
    latin: 'Vena Cava Superior',
    position: '-0.340815m 0.711882m 0.172959m',
    normal: '0m 1m 0m',
    target: '-0.340815m 0.711882m 0.172959m',
    orbit: '-40deg 65deg 1.2m'
  },
  {
    id: 'hotspot-pa',
    label: 'Pulmonary Arteries',
    latin: 'Arteriae Pulmonales',
    position: '0.419654m 0.412853m 0.077488m',
    normal: '0m 1m 0m',
    target: '0.05m 0.1m 0.03m',
    orbit: '100deg 85deg 2.0m'
  },
  {
    id: 'hotspot-aorta',
    label: 'Aorta',
    latin: 'Aorta',
    position: '0.101478m 0.622114m -0.123833m',
    normal: '0m 1m 0m',
    target: '0.01m 0.60m 0.03m',
    orbit: '-10deg 40deg 1.5m'
  }
];

function Heart() {
  const navigate = useNavigate();
  const modelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [hotspotSides, setHotspotSides] = useState({});
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);

  const handleBack = () => {
    navigate('/menu');
  };

  const focusOnPart = (hotspot) => {
    setActiveHotspot(hotspot.id);
    const viewer = modelRef.current;
    if (viewer) {
      viewer.setAttribute('camera-orbit', hotspot.orbit);
      viewer.setAttribute('camera-target', hotspot.target);
    }
  };

  const closeLabel = (e) => {
    e.stopPropagation();
    setActiveHotspot(null);
  };

  const getInternalScene = useCallback(() => {
    if (sceneRef.current && cameraRef.current) return { scene: sceneRef.current, camera: cameraRef.current };

    const mv = modelRef.current;
    if (!mv) return null;

    try {
      const allKeys = [...Object.getOwnPropertyNames(mv), ...Object.getOwnPropertySymbols(mv)];
      let scene = null;
      let camera = null;

      for (const key of allKeys) {
        const val = mv[key];
        if (!val || typeof val !== 'object') continue;
        if (val.isScene) scene = val;
        if (val.scene && val.scene.isScene) scene = val.scene;
        if (val.isCamera || val.isPerspectiveCamera) camera = val;
        if (val.camera && (val.camera.isCamera || val.camera.isPerspectiveCamera)) camera = val.camera;
      }

      if (scene) {
        sceneRef.current = scene;
        cameraRef.current = camera;
        return { scene, camera };
      }
    } catch (e) {
      console.error('Error accessing internal scene:', e);
    }
    return null;
  }, []);

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;

    mv.cameraControls = true;
    mv.setAttribute('camera-controls', '');

    const handleLoad = () => {
      setIsLoading(false);
      setTimeout(() => getInternalScene(), 500);
    };

    mv.addEventListener('load', handleLoad);
    if (mv.loaded) handleLoad();

    const timeout = setTimeout(() => setIsLoading(false), 10000);

    return () => {
      mv.removeEventListener('load', handleLoad);
      clearTimeout(timeout);
    };
  }, [getInternalScene]);

  useEffect(() => {
    let rafId = 0;
    const tick = () => {
      const mv = modelRef.current;
      const ctx = getInternalScene();

      if (mv && ctx?.camera) {
        const rect = mv.getBoundingClientRect();
        const newSides = {};

        HEART_HOTSPOTS.forEach(hotspot => {
          const posParts = hotspot.position.replace(/m/g, '').split(' ').map(parseFloat);
          const worldPos = new THREE.Vector3(posParts[0], posParts[1], posParts[2]);

          const ndc = worldPos.clone().project(ctx.camera);
          const screenX = (ndc.x + 1) / 2 * rect.width;

          newSides[hotspot.id] = screenX < (rect.width / 2) ? 'left' : 'right';
        });

        setHotspotSides(prev => {
          let changed = false;
          for (const id in newSides) {
            if (newSides[id] !== prev[id]) changed = true;
          }
          return changed ? newSides : prev;
        });
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [getInternalScene]);

  return (
    <div className="model-viewer-page skeleton-page">
      <button onClick={handleBack} className="back-btn">← Back to Menu</button>

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading 3D Heart...</p>
        </div>
      )}

      <model-viewer
        ref={modelRef}
        id="heartViewer"
        src="/models/heart.glb"
        ios-src="/models/heart.usdz"
        alt="3D Heart Model"
        ar
        camera-controls
        touch-action="none"
        ar-modes="webxr"
        camera-orbit="0deg 75deg 5m"
        min-camera-orbit="auto 15deg 1m"
        max-camera-orbit="auto 90deg 4m"
        field-of-view="45deg"
        shadow-intensity="1"
        interaction-prompt="auto"
        loading="eager"
        reveal="auto"
        quick-look-browsers="safari chrome"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          '--progress-bar-color': '#00E5FF',
          '--progress-bar-height': '4px',
        }}
      >
        {HEART_HOTSPOTS.map((hotspot) => {
          const isActive = activeHotspot === hotspot.id;
          const side = hotspotSides[hotspot.id] || 'right';

          return (
            <div
              key={hotspot.id}
              className={`skeleton-leader-hotspot ${side}`}
              slot={`hotspot-${hotspot.id.replace('hotspot-', '')}`}
              data-position={hotspot.position}
              data-normal={hotspot.normal}
              onClick={(e) => {
                e.stopPropagation();
                focusOnPart(hotspot);
              }}
              style={{ cursor: 'var(--cursor-pointer)' }}
            >
              <div className="skeleton-leader-dot" style={{ background: isActive ? '#fff' : '#00E5FF', borderColor: isActive ? '#00E5FF' : '#fff' }}></div>

              {isActive && (
                <>
                  <svg className="skeleton-leader-line-svg" xmlns="http://www.w3.org/2000/svg">
                    <line
                      x1={side === 'right' ? "0" : "100%"}
                      y1="50%"
                      x2={side === 'right' ? "100%" : "0"}
                      y2="50%"
                      stroke="#00E5FF"
                      strokeWidth="2"
                    />
                    <circle cx={side === 'right' ? "0" : "100%"} cy="50%" r="3" fill="#00E5FF" />
                  </svg>

                  <div className="skeleton-leader-label">
                    <div className="skeleton-leader-content">
                      <span className="skeleton-leader-english">{hotspot.label}</span>
                      <span className="skeleton-leader-latin">{hotspot.latin}</span>
                    </div>
                    <button className="skeleton-leader-close" onClick={closeLabel}>✕</button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </model-viewer>
    </div>
  );
}

export default Heart;
